<template>
  <div class="metadata-version-history">
    <!-- 返回按钮 (仅在详情视图显示) -->
    <div v-if="currentView === 'detail'" class="mb-4">
      <button class="btn btn-ghost gap-2" @click="backToList">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
        {{ $tt('返回版本列表') }}
      </button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- 版本列表视图 -->
    <div v-else-if="currentView === 'list' && versions.length > 0" class="space-y-3">
      <!-- 版本卡片 -->
      <div
        v-for="version in versions"
        :key="version.id"
        :class="[
          'card bg-base-100 shadow-md hover:shadow-xl transition-all cursor-pointer',
          version.is_current ? 'border-2 border-primary' : 'border border-base-300',
          selectedVersion?.id === version.id ? 'ring-2 ring-primary ring-offset-2' : ''
        ]"
        @click="selectVersion(version)">

        <div class="card-body p-4">
          <!-- 卡片头部:左侧信息 + 右侧操作 -->
          <div class="flex items-start justify-between gap-4">

            <!-- 左侧:版本信息 -->
            <div class="flex-1 min-w-0">
              <!-- 第一行:类型徽章 + 当前版本标记 + 版本号 -->
              <div class="flex items-center gap-2 mb-3 flex-wrap">
                <div class="badge badge-lg gap-1" :class="getOperationTypeClass(version.operation_type)">
                  <span>{{ getOperationTypeIcon(version.operation_type) }}</span>
                  <span>{{ version.operation_type_display }}</span>
                </div>
                <div v-if="version.is_current" class="badge badge-primary badge-outline">
                  {{ $tt('当前版本') }}
                </div>
                <div class="text-sm font-mono opacity-70">
                  v{{ version.version_number }}
                </div>
              </div>

              <!-- 第二行:时间 + 用户 + 来源 -->
              <div class="flex items-center gap-4 text-sm opacity-70 flex-wrap">
                <!-- 时间 -->
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span>{{ version.created_at }}</span>
                </div>

                <!-- 用户 -->
                <div v-if="version.created_by" class="flex items-center gap-1.5">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  <span>{{ version.created_by }}</span>
                </div>

                <!-- 来源 -->
                <div v-if="version.operation_source" class="flex items-center gap-1.5">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                  </svg>
                  <span>{{ version.operation_source }}</span>
                </div>
              </div>

              <!-- 第三行:变更摘要 -->
              <div v-if="version.change_summary" class="flex items-center gap-2 mt-3 flex-wrap">
                <div v-if="version.change_summary.added && version.change_summary.added.length > 0"
                     class="badge badge-success badge-sm gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                  {{ $tt('新增 {count}', { count: version.change_summary.added.length }) }}
                </div>
                <div v-if="version.change_summary.modified && version.change_summary.modified.length > 0"
                     class="badge badge-warning badge-sm gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                  {{ $tt('修改 {count}', { count: version.change_summary.modified.length }) }}
                </div>
                <div v-if="version.change_summary.deleted && version.change_summary.deleted.length > 0"
                     class="badge badge-error badge-sm gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
                  </svg>
                  {{ $tt('删除 {count}', { count: version.change_summary.deleted.length }) }}
                </div>
              </div>
            </div>

            <!-- 右侧:操作按钮 -->
            <div class="flex gap-2 flex-shrink-0">
              <button
                class="btn btn-sm btn-primary btn-outline gap-1"
                @click.stop="viewDiff(version)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
                {{ $tt('查看差异') }}
              </button>
              <button
                v-if="!version.is_current"
                class="btn btn-sm btn-warning btn-outline gap-1"
                @click.stop="restoreVersion(version)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
                </svg>
                {{ $tt('回退') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 无版本提示 -->
    <div v-else-if="currentView === 'list'" class="text-center py-20">
      <svg class="w-20 h-20 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      <p class="text-gray-500">{{ $tt('暂无版本历史') }}</p>
    </div>

    <!-- 版本详情视图 -->
    <div v-else-if="currentView === 'detail'" class="detail-view">
      <!-- 版本信息头 -->
      <div v-if="selectedVersion" class="grid grid-cols-2 gap-4 mb-6">
        <!-- 当前版本信息 -->
        <div class="card bg-primary/10 p-4">
          <h4 class="font-semibold mb-2 flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            {{ $tt('当前选中版本') }}
          </h4>
          <div class="space-y-1 text-sm">
            <div><span class="font-medium">{{ $tt('版本:') }}</span> v{{ selectedVersion.version_number }}</div>
            <div><span class="font-medium">{{ $tt('类型:') }}</span> <span class="badge badge-sm" :class="getOperationTypeClass(selectedVersion.operation_type)">{{ selectedVersion.operation_type_display }}</span></div>
            <div><span class="font-medium">{{ $tt('时间:') }}</span> {{ selectedVersion.created_at }}</div>
            <div v-if="selectedVersion.created_by"><span class="font-medium">{{ $tt('用户:') }}</span> {{ selectedVersion.created_by }}</div>
          </div>
        </div>

        <!-- 对比版本信息 -->
        <div v-if="compareVersion" class="card bg-base-200 p-4">
          <div class="flex justify-between items-start mb-2">
            <h4 class="font-semibold flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
              {{ $tt('对比版本') }} <span class="text-xs opacity-60">{{ $tt('(上一版本)') }}</span>
            </h4>
            <button
              class="btn btn-xs btn-warning gap-1"
              @click="handleRestoreCompareVersion"
              :title="$tt('回退到对比版本')">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
              </svg>
              {{ $tt('回退') }}
            </button>
          </div>
          <div class="space-y-1 text-sm">
            <div><span class="font-medium">{{ $tt('版本:') }}</span> v{{ compareVersion.version_number }}</div>
            <div><span class="font-medium">{{ $tt('类型:') }}</span> <span class="badge badge-sm">{{ compareVersion.operation_type }}</span></div>
            <div><span class="font-medium">{{ $tt('时间:') }}</span> {{ compareVersion.created_at }}</div>
          </div>
        </div>
      </div>

      <!-- 差异统计 -->
      <div v-if="diffData && diffData.changes" class="stats shadow mb-6 w-full">
        <div class="stat">
          <div class="stat-figure text-success">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
          </div>
          <div class="stat-title">{{ $tt('新增字段') }}</div>
          <div class="stat-value text-success">{{ diffData.changes.added?.length || 0 }}</div>
        </div>

        <div class="stat">
          <div class="stat-figure text-warning">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </div>
          <div class="stat-title">{{ $tt('修改字段') }}</div>
          <div class="stat-value text-warning">{{ diffData.changes.modified?.length || 0 }}</div>
        </div>

        <div class="stat">
          <div class="stat-figure text-error">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
            </svg>
          </div>
          <div class="stat-title">{{ $tt('删除字段') }}</div>
          <div class="stat-value text-error">{{ diffData.changes.deleted?.length || 0 }}</div>
        </div>
      </div>

      <!-- 字段对比详情 -->
      <div v-if="diffData && diffData.field_diffs">
        <h4 class="font-semibold mb-3 flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
          </svg>
          {{ $tt('字段详细对比') }}
        </h4>

        <div class="space-y-3">
          <div
            v-for="(diff, fieldName) in diffData.field_diffs"
            :key="fieldName"
            class="card bg-base-200 p-4">
            <!-- 字段名 -->
            <div class="font-medium mb-2 flex items-center gap-2">
              <span class="badge badge-outline">{{ getFieldDisplayName(fieldName) }}</span>
              <span class="badge badge-warning badge-sm">{{ $tt('已修改') }}</span>
            </div>

            <!-- 对比内容 -->
            <div class="grid grid-cols-2 gap-4">
              <!-- 旧值 -->
              <div class="bg-error/10 p-3 rounded">
                <div class="text-xs opacity-70 mb-1">{{ $tt('旧值') }}</div>
                <div v-if="fieldName === 'album_img' && diff.old" class="flex justify-center">
                    <img :src="diff.old" class="max-w-full max-h-48 object-contain rounded shadow-lg" :alt="$tt('旧封面')" />
                </div>
                <!-- 图片字段为空 -->
                <div v-else-if="fieldName === 'album_img' && !diff.old" class="text-center py-8 opacity-40 italic">
                    {{ $tt('(无封面)') }}
                </div>
                <div v-else class="font-mono text-sm break-all" :class="!diff.old ? 'opacity-40 italic' : ''">
                  {{ diff.old || $tt('(空)') }}
                </div>
              </div>

              <!-- 新值 -->
              <div class="bg-success/10 p-3 rounded">
                <div class="text-xs opacity-70 mb-1">{{ $tt('新值') }}</div>
                <!-- 图片字段 -->
                <div v-if="fieldName === 'album_img' && diff.new" class="flex justify-center">
                    <img :src="diff.new" class="max-w-full max-h-48 object-contain rounded shadow-lg" :alt="$tt('新封面')" />
                  </div>
                  <!-- 图片字段为空 -->
                  <div v-else-if="fieldName === 'album_img' && !diff.new" class="text-center py-8 opacity-40 italic">
                    {{ $tt('(无封面)') }}
                  </div>
                <div v-else class="font-mono text-sm break-all" :class="!diff.new ? 'opacity-40 italic' : ''">
                  {{ diff.new || $tt('(空)') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 无差异提示 -->
      <div v-if="diffData && Object.keys(diffData.field_diffs || {}).length === 0" class="text-center py-10">
        <svg class="w-16 h-16 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="text-gray-500">{{ $tt('该版本与对比版本无差异') }}</p>
      </div>

      <!-- 底部操作按钮 -->
      <div class="flex justify-end gap-2 mt-6">
        <button class="btn btn-ghost" @click="backToList">{{ $tt('关闭') }}</button>
        <button
          v-if="selectedVersion && !selectedVersion.is_current"
          class="btn btn-warning"
          @click="restoreVersion(selectedVersion)">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
          </svg>
          {{ $tt('回退到此版本') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MetadataVersionHistory',
  data() {
    return {
      trackInfo: null,
      versions: [],
      selectedVersion: null,
      loading: false,
      trackId: null,
      currentView: 'list', // 'list' 或 'detail'
      diffData: null,
      compareVersion: null
    }
  },
  methods: {
    // 打开版本历史(现在用于加载数据而不是打开对话框)
    async open(trackId, trackName, trackPath) {
      this.trackId = trackId
      this.trackInfo = {
        id: trackId,
        name: trackName,
        path: trackPath
      }
      this.currentView = 'list'
      await this.loadVersions()
    },

    // 加载版本历史
    async loadVersions() {
      this.loading = true
      try {
        const res = await this.$api.Task.getMetadataVersionHistory({
          path: this.trackInfo.path
        })
        console.log(res)
        if (res.result) {
          this.versions = res.data.versions || []
          this.trackInfo = res.data.track || this.trackInfo
        }
      } catch (error) {
        console.error('加载版本历史失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 选中版本
    selectVersion(version) {
      this.selectedVersion = version
    },

    // 查看差异
    async viewDiff(version) {
      this.selectedVersion = version
      this.currentView = 'detail'
      this.diffData = null
      await this.loadDiff()
    },

    // 加载差异数据
    async loadDiff() {
      this.loading = true
      try {
        const res = await this.$api.Task.getMetadataVersionDiff({
          id: this.selectedVersion.id
        })

        if (res.result) {
          this.diffData = res.data
          this.compareVersion = {
            version_number: this.diffData.version1?.version_number,
            created_at: this.diffData.version1?.created_at,
            operation_type: this.diffData.version1?.operation_type,
            id: this.diffData.version1?.id
          }
        }
      } catch (error) {
        console.error('加载版本差异失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 返回列表视图
    backToList() {
      this.currentView = 'list'
      this.selectedVersion = null
      this.diffData = null
      this.compareVersion = null
    },

    // 回退版本
    async restoreVersion(version) {
      try {
        const res = await this.$api.Task.restoreMetadataVersion({
          id: version.id,
          restored_by: this.$store.state.user?.username || 'unknown'
        })

        if (res.result) {
          this.$myMsg.notify({
                            title: this.$tt('版本回退成功'),
                            type: 'success',
                            time: 3000,
                        });
          // 回到列表视图并刷新
          this.backToList()
          await this.loadVersions()
          this.$emit('restored', version)
        } else {
          this.$myMsg.notify({
                            title: this.$tt('版本回退失败'),
                            content: res.message,
                            type: 'success',
                            time: 3000,
                        });
        }
      } catch (error) {
      }
    },

    // 处理回退对比版本
    async handleRestoreCompareVersion() {
      if (!this.compareVersion?.id) {
        return
      }

      // 构造对比版本对象
      const compareVersionObj = {
        id: this.compareVersion.id,
        version_number: this.compareVersion.version_number,
        created_at: this.compareVersion.created_at,
        operation_type: this.compareVersion.operation_type,
        is_current: false
      }

      await this.restoreVersion(compareVersionObj)
    },

    // 获取操作类型样式
    getOperationTypeClass(type) {
      const classMap = {
        'manual': 'badge-primary',
        'scrape': 'badge-success',
        'batch': 'badge-info',
        'import': 'badge-warning',
        'restore': 'badge-secondary',
        'plugin': 'badge-accent'
      }
      return classMap[type] || 'badge-ghost'
    },

    // 获取操作类型图标
    getOperationTypeIcon(type) {
      const iconMap = {
        'manual': '✏️',
        'scrape': '🔍',
        'batch': '📦',
        'import': '📥',
        'restore': '⏮️',
        'plugin': '🔌'
      }
      return iconMap[type] || '📄'
    },

    // 获取字段显示名称
    getFieldDisplayName(fieldName) {
      const nameMap = {
        'title': '标题',
        'artist': '艺术家',
        'album': '专辑',
        'albumartist': '专辑艺术家',
        'year': '年份',
        'genre': '风格',
        'tracknumber': '音轨号',
        'discnumber': 'CD号',
        'totaltracks': '总音轨数',
        'totaldiscs': '总CD数',
        'lyrics': '歌词',
        'comment': '简介',
        'description': '描述',
        'language': '语言',
        'composer': '作曲家',
        'lyricist': '作词家',
        'albumversion': '专辑版本',
        'label': '标签',
        'album_type': '专辑类型',
        'album_cover_hash': '封面',
        "lyrics_file": "歌词文件"
      }
      return this.$tt(nameMap[fieldName] || fieldName)
    }
  }
}
</script>

<style scoped>
/* 卡片悬停效果 */
.card:hover {
  transform: translateY(-2px);
}
</style>
